import { CircularProgress, Typography, FormControl, FormHelperText } from "@material-ui/core";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { useGetList, useNotify } from "react-admin";
import { Field } from "react-final-form";

import { DeviationArticleStatus } from "../../../entities/DeviationArticleStatus";
import { compareArticles } from "./articleSyncUtils";
import { ConflictModal } from "./ConflictModal";
import { SyncTable } from "./SyncTable";

export const validateArticleSyncInput = (deviationArticles) => {
	const hasUnresolvedConflicts = deviationArticles.some(
		(article) =>
			article.article_status === DeviationArticleStatus.CONFLICT && !article.resolved_units
	);

	if (hasUnresolvedConflicts) {
		return "There are unresolved article conflicts. Please resolve them.";
	}

	return undefined;
};

const ArticleSyncInputContent = ({ input, meta, orderRecord, ...props }) => {
	const notify = useNotify();

	const deviationArticles = input.value;
	const { onChange } = input;

	const { touched, error, invalid } = meta;

	const {
		data: fetchedOrderData,
		isLoading,
		error: orderArticlesError,
	} = useGetList(
		"order-pricelist",
		{ page: 0, perPage: -1 },
		{ field: "id", order: "ASC" },
		{ id: orderRecord?.id },
		{
			enabled: !!orderRecord?.id,
		}
	);

	const orderArticles = fetchedOrderData ? Object.values(fetchedOrderData) : [];

	const [articleToResolve, setArticleToResolve] = useState(null);

	useEffect(() => {
		if (isLoading || !deviationArticles || !orderRecord?.id) {
			return;
		}
		const comparisonResult = compareArticles(deviationArticles, orderArticles);
		if (!isEqual(deviationArticles, comparisonResult)) {
			onChange(comparisonResult);
		}
	}, [orderArticles, deviationArticles, isLoading, orderRecord?.id, onChange]);

	const handleOpenConflictModal = (articleItem) => setArticleToResolve(articleItem);

	const handleCloseConflictModal = () => setArticleToResolve(null);

	const handleResolveConflict = (article_code, resolvedUnits) => {
		const updatedArticlesMap = deviationArticles.map((article) => {
			if (article.article_code === article_code) {
				return {
					...article,
					resolved_units: resolvedUnits,
					article_status: DeviationArticleStatus.RESOLVED,
				};
			}
			return article;
		});
		onChange(updatedArticlesMap);
		notify("Resolution saved. It will be applied on form submission.", { type: "info" });
		handleCloseConflictModal();
	};

	if (isLoading) return <CircularProgress size={25} />;
	if (orderArticlesError)
		return (
			<Typography color="error">
				Could not load order articles. Error: {orderArticlesError?.message || "Unknown error"}
			</Typography>
		);

	if (!deviationArticles.length && !invalid) {
		return (
			<Typography variant="caption" color="textSecondary">
				No articles in deviation or order to synchronize.
			</Typography>
		);
	}

	return (
		<FormControl fullWidth margin="normal" error={invalid}>
			<Typography variant="h5" gutterBottom>
				Articles Synchronization
			</Typography>
			{touched && invalid && (
				<FormHelperText>
					<Typography variant="h6" gutterBottom>
						{error}
					</Typography>
				</FormHelperText>
			)}

			<SyncTable articles={deviationArticles} onResolveClick={handleOpenConflictModal} />

			{articleToResolve && (
				<ConflictModal
					isOpen={!!articleToResolve}
					onClose={handleCloseConflictModal}
					onResolve={handleResolveConflict}
					deviationArticle={articleToResolve}
				/>
			)}
		</FormControl>
	);
};

export const ArticleSyncInput = ({ source, orderRecord, ...props }) => {
	return (
		<Field
			name={source}
			validate={validateArticleSyncInput}
			render={({ input, meta }) => (
				<ArticleSyncInputContent input={input} meta={meta} orderRecord={orderRecord} {...props} />
			)}
		/>
	);
};
